import { useEffect, useState, type ComponentType } from 'react';
import { Box } from '@mui/material';
import { REACT_ICON_PACKS, type EntityIcon, type ReactIconPack } from '@wedding/shared';

type IconComponent = ComponentType<{ size?: number | string }>;

function isIconComponent(value: unknown): value is IconComponent {
  return typeof value === 'function';
}

const PACK_LOADERS = {
  ai: () => import('react-icons/ai'),
  bi: () => import('react-icons/bi'),
  bs: () => import('react-icons/bs'),
  cg: () => import('react-icons/cg'),
  ci: () => import('react-icons/ci'),
  di: () => import('react-icons/di'),
  fa: () => import('react-icons/fa'),
  fa6: () => import('react-icons/fa6'),
  fc: () => import('react-icons/fc'),
  fi: () => import('react-icons/fi'),
  gi: () => import('react-icons/gi'),
  go: () => import('react-icons/go'),
  gr: () => import('react-icons/gr'),
  hi: () => import('react-icons/hi'),
  hi2: () => import('react-icons/hi2'),
  im: () => import('react-icons/im'),
  io: () => import('react-icons/io'),
  io5: () => import('react-icons/io5'),
  lia: () => import('react-icons/lia'),
  lu: () => import('react-icons/lu'),
  md: () => import('react-icons/md'),
  pi: () => import('react-icons/pi'),
  ri: () => import('react-icons/ri'),
  rx: () => import('react-icons/rx'),
  si: () => import('react-icons/si'),
  sl: () => import('react-icons/sl'),
  tb: () => import('react-icons/tb'),
  tfi: () => import('react-icons/tfi'),
  ti: () => import('react-icons/ti'),
  vsc: () => import('react-icons/vsc'),
  wi: () => import('react-icons/wi'),
} satisfies Record<ReactIconPack, () => Promise<unknown>>;

function loadPack(pack: string): (() => Promise<unknown>) | undefined {
  if (!(REACT_ICON_PACKS as readonly string[]).includes(pack)) return undefined;
  return PACK_LOADERS[pack as ReactIconPack];
}

export async function reactIconExists(pack: string, name: string): Promise<boolean> {
  const load = loadPack(pack);
  if (!load) return false;
  try {
    const mod = await load() as Record<string, unknown>;
    return isIconComponent(mod[name]);
  } catch {
    return false;
  }
}

function ReactPackIcon({ pack, name, size }: { pack: string; name: string; size: number }) {
  const [Icon, setIcon] = useState<IconComponent | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = loadPack(pack);
    if (!load) return undefined;
    load()
      .then((mod) => {
        const found = (mod as Record<string, unknown>)[name];
        if (!cancelled && isIconComponent(found)) setIcon(() => found);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [pack, name]);

  if (!Icon) return null;
  return <Icon size={size} />;
}

export default function EntityIconMark({
  icon,
  size = 20,
}: {
  icon: EntityIcon | null | undefined;
  size?: number;
}) {
  if (!icon) return null;
  const frame = {
    width: size,
    height: size,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
    lineHeight: 1,
    overflow: 'hidden',
  } as const;

  if (icon.type === 'emoji') {
    return (
      <Box component="span" sx={{ ...frame, fontSize: size * 0.85 }}>
        {icon.value}
      </Box>
    );
  }
  if (icon.type === 'image') {
    return (
      <Box
        component="img"
        src={icon.url}
        alt=""
        sx={{ ...frame, objectFit: 'contain' }}
      />
    );
  }
  if (icon.type === 'svg') {
    return (
      <Box
        component="span"
        sx={{
          ...frame,
          '& svg': { width: '100%', height: '100%', display: 'block' },
        }}
        dangerouslySetInnerHTML={{ __html: icon.value }}
      />
    );
  }
  return (
    <Box component="span" sx={frame}>
      <ReactPackIcon pack={icon.pack} name={icon.name} size={size} />
    </Box>
  );
}
